import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { PageHeader } from '../components/PageHeader';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { propertyLine, propertyTypeLabels, residenceText } from './labels';
import { ResidenceDialog } from './ResidenceDialog';

// OwnerPage: ein Eigentümer mit Nummern, E-Mail-Adressen, Wohnort und allen seinen Objekten (OFFENE_FRAGEN B-17). Jedes
// Objekt gehört zu seinem Gebiet und Makler – der Wohnort des Eigentümers zählt dafür nie.
export function OwnerPage() {
  const { ownerId = '' } = useParams();
  const { project, can } = useProject();
  const papi = useProjectApi();
  const owner = useQuery({ queryKey: projectKeys.owner(project.id, ownerId), queryFn: ({ signal }) => papi.getOwner(ownerId, signal) });
  const [editing, setEditing] = useState(false);
  if (owner.isPending) return <PageLoader />;
  if (owner.isError) return <ErrorMessage error={owner.error} onRetry={() => void owner.refetch()} />;
  const o = owner.data;
  const count = o.properties.length;
  return (
    <div className="page owner-page">
      <PageHeader title={o.name} description={`Eigentümer · ${count === 1 ? 'ein Objekt' : `${count} Objekte`}`} />
      <section className="card">
        <h2>Kontakt</h2>
        <dl className="facts">
          <dt>Telefon</dt>
          <dd>
            {o.phones.length === 0
              ? '–'
              : o.phones.map((p) => (
                  <a key={p.number} className="owner-channel" href={`tel:${p.e164 ?? p.number}`}>
                    {p.number}
                    {p.type === 'MOBILE' && ' (mobil)'}
                  </a>
                ))}
          </dd>
          <dt>E-Mail</dt>
          <dd>
            {o.emails.length === 0
              ? '–'
              : o.emails.map((e) => (
                  <a key={e} className="owner-channel" href={`mailto:${e}`}>
                    {e}
                  </a>
                ))}
          </dd>
          <dt>Wohnort</dt>
          <dd className="owner-residence">
            {residenceText(o.residence)}
            {o.residence.source === 'AUTO' && o.residence.reason && <span className="muted"> ({o.residence.reason})</span>}
            {can('lead.edit') && count > 0 && (
              <button type="button" className="btn btn-small" onClick={() => setEditing(true)}>
                Wohnort ändern
              </button>
            )}
          </dd>
        </dl>
      </section>
      <section className="card">
        <h2>Objekte</h2>
        {count === 0 ? (
          <p className="muted">Keine aktiven Objekte.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Objekt</th>
                <th>Art</th>
                <th>Status</th>
                <th>Gebiet</th>
                <th>Makler</th>
              </tr>
            </thead>
            <tbody>
              {o.properties.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={`/p/${project.key}/leads/${p.id}`}>{propertyLine(p)}</Link>
                  </td>
                  <td>{(p.property_type && propertyTypeLabels[p.property_type]) || '–'}</td>
                  <td>
                    <span className="badge badge-muted">{p.status_label}</span>
                  </td>
                  <td>{p.area_name ?? '–'}</td>
                  <td>{p.field_sales_name ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {editing && <ResidenceDialog owner={o} leadId={o.residence.lead_id ?? o.properties[0]?.id ?? ''} onClose={() => setEditing(false)} />}
    </div>
  );
}
